import React, { useRef, useState } from 'react';
import { Box, Typography, IconButton, CircularProgress } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import api from '../api';

const QuestionImageUploader = ({ value, onChange, disabled }) => {
  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const upload = async (file) => {
    if (!file || !String(file.type || '').startsWith('image/')) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const res = await api.post('/admin/question-bank/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const url = res.data?.data?.image_url || '';
      if (url) onChange(url);
    } catch {
      // silently fail — parent can show toast if needed
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Box>
      <input
        type="file"
        accept="image/png,image/jpeg,image/webp"
        style={{ display: 'none' }}
        ref={fileInputRef}
        disabled={disabled || uploading}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
      />
      {value ? (
        <Box sx={{ display: 'inline-flex', position: 'relative', mb: 1 }}>
          <img
            src={value}
            alt="question visual"
            style={{ maxWidth: 240, maxHeight: 160, borderRadius: 6, border: '1px solid #e0e0e0', objectFit: 'contain', display: 'block' }}
            onError={(e) => { e.target.style.opacity = '0.3'; }}
          />
          <IconButton
            size="small"
            disabled={disabled}
            onClick={() => onChange('')}
            sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.9)', color: '#f44336', p: 0.3 }}
          >
            <CloseIcon sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      ) : null}
      <Box
        onDragOver={(e) => { if (e.dataTransfer?.types?.includes('Files')) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
        onDrop={(e) => { const f = e.dataTransfer?.files?.[0]; if (!f) return; e.preventDefault(); setDragOver(false); upload(f); }}
        onClick={() => !disabled && !uploading && fileInputRef.current?.click()}
        sx={{
          border: '1px dashed',
          borderColor: dragOver ? 'primary.main' : 'divider',
          bgcolor: dragOver ? 'rgba(99,102,241,0.05)' : 'grey.50',
          borderRadius: 1.5,
          px: 2,
          py: 1.25,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          cursor: disabled || uploading ? 'not-allowed' : 'pointer',
          '&:hover': disabled || uploading ? {} : { borderColor: 'primary.main', bgcolor: 'rgba(99,102,241,0.03)' },
        }}
      >
        {uploading ? (
          <CircularProgress size={18} />
        ) : (
          <>
            <CloudUploadIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
            <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary', fontSize: '0.78rem' }}>
              {value ? 'Drop to replace, or click to browse' : 'Drop image here, or click to browse'}
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};

export default QuestionImageUploader;
